// Conținut real vorba.dev, trilingv (en/ro/de). Getters pe locale.
// `key`/`href`/`slug`/`linkedin`/nume proprii = agnostice de limbă. RO + DE draftate de
// mine — DE de revizuit pentru CH (ortografie elvețiană: ss, nu ß).

import { DEFAULT_LOCALE } from './i18n/index.js'

// Demo-urile interactive (Portfolio). tag + tagline se traduc; restul rămâne.
const DEMOS_I18N = [
  {
    key: 'close-cockpit',
    title: 'Close Cockpit',
    href: '/demos/close-cockpit',
    i18n: {
      en: { tag: 'Financial close', tagline: 'The whole group close on one board.' },
      ro: { tag: 'Închidere financiară', tagline: 'Toată închiderea de grup pe un singur tablou.' },
      de: { tag: 'Finanzabschluss', tagline: 'Der ganze Konzernabschluss auf einem Board.' },
    },
  },
  {
    key: 'excel-to-app',
    title: 'Excel → App',
    href: '/demos/excel-to-app',
    i18n: {
      en: { tag: 'Internal tools', tagline: 'Your messy spreadsheet, turned into a tool you own.' },
      ro: { tag: 'Unelte interne', tagline: 'Fișierul tău Excel haotic, transformat într-o unealtă a ta.' },
      de: { tag: 'Interne Tools', tagline: 'Ihre chaotische Tabelle, verwandelt in ein Tool, das Ihnen gehört.' },
    },
  },
]

// Departamentele B2B (secțiunea Services pe homepage). `slug` = link stabil; `name` localizat.
const SERVICES_I18N = [
  { slug: 'finance', name: { en: 'Finance', ro: 'Finanțe', de: 'Finanzen' }, desc: { en: 'month-end close, reporting, reconciliations', ro: 'închidere de lună, raportare, reconcilieri', de: 'Monatsabschluss, Reporting, Abstimmungen' } },
  { slug: 'operations', name: { en: 'Operations', ro: 'Operațiuni', de: 'Betrieb' }, desc: { en: 'internal tools, dashboards, workflows', ro: 'unelte interne, dashboard-uri, fluxuri', de: 'interne Tools, Dashboards, Workflows' } },
  { slug: 'sales', name: { en: 'Sales', ro: 'Vânzări', de: 'Vertrieb' }, desc: { en: 'CRM sync, lead routing, quotes', ro: 'sincronizare CRM, rutare lead-uri, oferte', de: 'CRM-Sync, Lead-Routing, Offerten' } },
  { slug: 'people', name: { en: 'People', ro: 'Resurse umane', de: 'Personal' }, desc: { en: 'onboarding, approvals, records', ro: 'onboarding, aprobări, dosare', de: 'Onboarding, Freigaben, Dossiers' } },
  { slug: 'procurement', name: { en: 'Procurement', ro: 'Achiziții', de: 'Einkauf' }, desc: { en: 'purchase orders, approvals, spend control', ro: 'comenzi, aprobări, control cheltuieli', de: 'Bestellungen, Freigaben, Ausgabenkontrolle' } },
]

// Fondatorii — fiecare ca intrare de dicționar. Etichetele gloselor (in vorba / in code /
// in life) rămân ca artificiu de brand; doar textele se traduc.
const FOUNDERS_I18N = [
  {
    name: 'Alex Apostol',
    linkedin: 'https://www.linkedin.com/in/alexandru-apostol/',
    i18n: {
      en: {
        entry: 'n. masc. · group reporting & finance · Zürich',
        glosses: [
          ['in vorba', "Listens. Asks the questions that don't sound like a discovery call."],
          ['in code', 'Builds the business case. The numbers have to convince your board.'],
          ['in life', 'Ran group reporting at ABB on $32bn in revenue, PwC and J&J before that. MBA at St. Gallen, a Romanian at home in Zürich.'],
        ],
      },
      ro: {
        entry: 'subst. masc. · raportare de grup & finanțe · Zürich',
        glosses: [
          ['in vorba', 'Ascultă. Pune întrebările care nu sună a discovery call.'],
          ['in code', 'Construiește business case-ul. Cifrele trebuie să-ți convingă board-ul.'],
          ['in life', 'A condus raportarea de grup la ABB pe 32 mld. $ venituri, PwC și J&J înainte. MBA la St. Gallen, un român ca acasă la Zürich.'],
        ],
      },
      de: {
        entry: 'Subst. mask. · Konzern-Reporting & Finanzen · Zürich',
        glosses: [
          ['in vorba', 'Hört zu. Stellt die Fragen, die nicht nach Discovery-Call klingen.'],
          ['in code', 'Baut den Business Case. Die Zahlen müssen Ihren Verwaltungsrat überzeugen.'],
          ['in life', 'Leitete das Konzern-Reporting bei ABB mit 32 Mrd. $ Umsatz, davor PwC und J&J. MBA an der HSG St. Gallen, ein Rumäne zu Hause in Zürich.'],
        ],
      },
    },
  },
  {
    name: 'Matei Chetreanu',
    linkedin: 'https://www.linkedin.com/in/matei-chetreanu/',
    i18n: {
      en: {
        entry: 'n. masc. · full-stack developer · Bucharest',
        glosses: [
          ['in vorba', 'Turns the conversation into a build plan. Has a tool for every problem.'],
          ['in code', 'Full-stack. Designs the data model, builds the interface, ships it.'],
          ['in life', 'Bucharest. Builds custom where others assemble templates.'],
        ],
      },
      ro: {
        entry: 'subst. masc. · full-stack developer · București',
        glosses: [
          ['in vorba', 'Transformă discuția într-un plan de build. Are o unealtă pentru orice problemă.'],
          ['in code', 'Full-stack. Proiectează modelul de date, construiește interfața, o livrează.'],
          ['in life', 'București. Construiește custom acolo unde alții asamblează template-uri.'],
        ],
      },
      de: {
        entry: 'Subst. mask. · Full-Stack-Entwickler · Bukarest',
        glosses: [
          ['in vorba', 'Verwandelt das Gespräch in einen Bauplan. Hat für jedes Problem ein Tool.'],
          ['in code', 'Full-Stack. Entwirft das Datenmodell, baut das Interface, liefert es aus.'],
          ['in life', 'Bukarest. Baut massgeschneidert, wo andere Templates zusammenstecken.'],
        ],
      },
    },
  },
]

const pick = (obj, locale) => obj[locale] || obj[DEFAULT_LOCALE]

export function getDemos(locale = DEFAULT_LOCALE) {
  return DEMOS_I18N.map((d) => ({ key: d.key, title: d.title, href: d.href, ...pick(d.i18n, locale) }))
}

export function getServices(locale = DEFAULT_LOCALE) {
  return SERVICES_I18N.map((s) => ({ slug: s.slug, name: pick(s.name, locale), desc: pick(s.desc, locale) }))
}

export function getFounders(locale = DEFAULT_LOCALE) {
  return FOUNDERS_I18N.map((f) => ({ name: f.name, linkedin: f.linkedin, ...pick(f.i18n, locale) }))
}
